import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, FileDown, RotateCcw, Phone } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";

type IntakeState = Record<string, string>;

const SECTIONS: {
  title: string;
  fields: { key: string; label: string; type?: "text" | "textarea" | "number" | "date"; placeholder?: string; colSpan?: 1 | 2 | 3 | 4 }[];
}[] = [
  {
    title: "Call & Seller Info",
    fields: [
      { key: "call_date", label: "Call Date", type: "date", colSpan: 1 },
      { key: "call_time", label: "Call Time", placeholder: "e.g. 2:30 PM", colSpan: 1 },
      { key: "instrument_type", label: "Instrument Type", placeholder: "Land Contract / Note / DOT", colSpan: 2 },
      { key: "seller_name", label: "Seller Name", colSpan: 2 },
      { key: "seller_phone", label: "Seller Phone", colSpan: 1 },
      { key: "seller_email", label: "Seller Email", colSpan: 1 },
      { key: "seller_address", label: "Seller Mailing Address", colSpan: 4 },
      { key: "referral_source", label: "How did they hear about us?", colSpan: 4 },
    ],
  },
  {
    title: "Property Details",
    fields: [
      { key: "property_address", label: "Property Address", colSpan: 4 },
      { key: "property_type", label: "Property Type", placeholder: "SFR / Land / Mobile / Commercial", colSpan: 2 },
      { key: "occupancy", label: "Occupancy", placeholder: "Owner / Tenant / Vacant", colSpan: 1 },
      { key: "county_state", label: "County / State", colSpan: 1 },
      { key: "estimated_value", label: "Estimated Value ($)", type: "number", colSpan: 1 },
      { key: "condition", label: "Condition", placeholder: "Good / Fair / Poor", colSpan: 1 },
      { key: "insured", label: "Insured?", placeholder: "Yes / No", colSpan: 1 },
      { key: "taxes_current", label: "Taxes Current?", placeholder: "Yes / No", colSpan: 1 },
    ],
  },
  {
    title: "Original Contract Terms",
    fields: [
      { key: "orig_sale_date", label: "Original Sale Date", type: "date", colSpan: 1 },
      { key: "orig_sale_price", label: "Original Sale Price ($)", type: "number", colSpan: 1 },
      { key: "orig_down_payment", label: "Down Payment ($)", type: "number", colSpan: 1 },
      { key: "orig_financed", label: "Amount Financed ($)", type: "number", colSpan: 1 },
      { key: "orig_interest_rate", label: "Interest Rate (%)", type: "number", colSpan: 1 },
      { key: "orig_term", label: "Term (months)", type: "number", colSpan: 1 },
      { key: "orig_monthly_pmt", label: "Monthly Payment ($)", type: "number", colSpan: 1 },
      { key: "orig_pmt_includes", label: "P&I only? Or T&I?", colSpan: 1 },
      { key: "balloon", label: "Balloon Payment?", placeholder: "Yes / No + amount + date", colSpan: 4 },
    ],
  },
  {
    title: "Current Status",
    fields: [
      { key: "current_balance", label: "Current Balance ($)", type: "number", colSpan: 1 },
      { key: "next_due_date", label: "Next Due Date", type: "date", colSpan: 1 },
      { key: "payments_made", label: "# Payments Made", type: "number", colSpan: 1 },
      { key: "payments_remaining", label: "# Payments Left", type: "number", colSpan: 1 },
      { key: "late_history", label: "Late / Missed Payments", colSpan: 2 },
      { key: "servicing", label: "Servicing (self / company)", colSpan: 2 },
    ],
  },
  {
    title: "Buyer / Payor",
    fields: [
      { key: "buyer_name", label: "Buyer Name", colSpan: 2 },
      { key: "buyer_credit", label: "Credit Score (est.)", colSpan: 1 },
      { key: "buyer_employment", label: "Employment", colSpan: 1 },
      { key: "buyer_phone", label: "Buyer Phone", colSpan: 2 },
      { key: "buyer_notes", label: "Buyer Notes", colSpan: 2 },
    ],
  },
  {
    title: "Seller Motivation & Offer",
    fields: [
      { key: "reason_selling", label: "Reason for Selling", type: "textarea", colSpan: 4 },
      { key: "amount_needed", label: "Amount Seller Needs ($)", type: "number", colSpan: 2 },
      { key: "partial_full", label: "Full or Partial Purchase?", colSpan: 2 },
      { key: "quoted_offer", label: "Quoted Offer ($)", type: "number", colSpan: 2 },
      { key: "timeline", label: "Timeline to Close", colSpan: 2 },
      { key: "notes", label: "Additional Notes", type: "textarea", colSpan: 4 },
    ],
  },
];

const EMPTY: IntakeState = Object.fromEntries(
  SECTIONS.flatMap((s) => s.fields.map((f) => [f.key, ""]))
);

export function CallIntakeForm() {
  const [data, setData] = useState<IntakeState>(EMPTY);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const allFields = useMemo(() => SECTIONS.flatMap((s) => s.fields), []);
  const labelFor = (k: string) => allFields.find((f) => f.key === k)?.label || k;

  const setField = (k: string, v: string) => setData((d) => ({ ...d, [k]: v }));

  const reset = () => {
    if (confirm("Clear the form?")) setData(EMPTY);
  };

  const buildPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 28;
    const usableW = pageW - margin * 2;
    let y = margin;

    // Compact header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Land Contract / Note — Phone Intake", margin, y + 2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text("CFG Investor  •  Mike  •  517-908-0433", margin, y + 12);
    doc.text(
      `${new Date().toLocaleString()}`,
      pageW - margin,
      y + 12,
      { align: "right" }
    );
    y += 18;
    doc.setDrawColor(180);
    doc.line(margin, y, pageW - margin, y);
    y += 4;

    // 3-column layout for compactness
    const cols = 3;
    const colW = usableW / cols;
    const rowH = 22;
    const labelSize = 6.5;
    const valueSize = 9;

    const drawCell = (x: number, cy: number, w: number, h: number, label: string, value: string) => {
      doc.setDrawColor(210);
      doc.setLineWidth(0.4);
      doc.rect(x, cy, w, h);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelSize);
      doc.setTextColor(110);
      doc.text(label.toUpperCase(), x + 3, cy + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(valueSize);
      doc.setTextColor(0);
      if (value) {
        const wrapped = doc.splitTextToSize(value, w - 6) as string[];
        doc.text(wrapped.slice(0, h > rowH ? 3 : 1), x + 3, cy + 15);
      }
    };

    SECTIONS.forEach((section) => {
      // Compact section header bar
      doc.setFillColor(230, 236, 248);
      doc.rect(margin, y, usableW, 11, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(30, 50, 90);
      doc.text(section.title.toUpperCase(), margin + 4, y + 8);
      doc.setTextColor(0);
      y += 12;

      let col = 0;
      section.fields.forEach((f) => {
        const span = Math.min(f.colSpan || 1, cols);
        // Map 4-col design → 3-col: colSpan 4 -> full row, 3 -> 2 cols, else 1
        const cellCols = span >= 3 ? cols : span === 2 ? 2 : 1;
        const cellW = colW * cellCols;
        const cellH = f.type === "textarea" ? rowH * 1.5 : rowH;

        if (col + cellCols > cols) {
          y += rowH;
          col = 0;
        }
        const x = margin + col * colW;
        drawCell(x, y, cellW, cellH, f.label, data[f.key] || "");
        col += cellCols;
        if (f.type === "textarea") {
          // textareas are full-width; advance extra
          y += cellH;
          col = 0;
        } else if (col >= cols) {
          y += rowH;
          col = 0;
        }
      });
      if (col !== 0) {
        y += rowH;
        col = 0;
      }
      y += 3;
    });

    return doc;
  };

  const previewPdf = () => {
    try {
      const doc = buildPdf();
      // Use data URI + in-app Dialog iframe. Blob URLs opened via window.open
      // are blocked by Edge / preview-iframe sandboxes.
      const uri = doc.output("datauristring");
      setPreviewUrl(uri);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF preview");
    }
  };

  const downloadPdf = () => {
    try {
      const doc = buildPdf();
      const name = (data.seller_name || "intake").replace(/[^a-z0-9]+/gi, "_");
      doc.save(`call_intake_${name}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Phone className="h-6 w-6" />
            Call Intake — Land Contract / Note Pricing
          </h2>
          <p className="text-muted-foreground">
            Fill this out live during the call. Preview or download the PDF to print.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={reset}>
            <RotateCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          <Button variant="outline" onClick={previewPdf}>
            <Printer className="mr-2 h-4 w-4" /> Preview PDF
          </Button>
          <Button onClick={downloadPdf}>
            <FileDown className="mr-2 h-4 w-4" /> Download PDF
          </Button>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <Card key={section.title}>
          <CardHeader className="py-3">
            <CardTitle className="text-base">{section.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {section.fields.map((f) => {
                const span = f.colSpan || 1;
                const spanClass =
                  span === 4 ? "col-span-4" : span === 3 ? "col-span-3" : span === 2 ? "col-span-2" : "col-span-1";
                return (
                  <div key={f.key} className={spanClass}>
                    <Label htmlFor={f.key}>{f.label}</Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        id={f.key}
                        value={data[f.key]}
                        onChange={(e) => setField(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        rows={3}
                      />
                    ) : (
                      <Input
                        id={f.key}
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        value={data[f.key]}
                        onChange={(e) => setField(f.key, e.target.value)}
                        placeholder={f.placeholder}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}

      <Separator />
      <div className="flex justify-end gap-2 pb-8">
        <Button variant="outline" onClick={previewPdf}>
          <Printer className="mr-2 h-4 w-4" /> Preview PDF
        </Button>
        <Button onClick={downloadPdf}>
          <FileDown className="mr-2 h-4 w-4" /> Download PDF
        </Button>
      </div>

      <Dialog open={!!previewUrl} onOpenChange={(o) => !o && setPreviewUrl(null)}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>PDF Preview</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <Button size="sm" onClick={downloadPdf}>
              <FileDown className="mr-2 h-4 w-4" /> Download
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (!previewUrl) return;
                const w = window.open("");
                if (w) {
                  w.document.write(
                    `<iframe src="${previewUrl}" style="border:0;width:100%;height:100vh"></iframe>`
                  );
                } else {
                  toast.error("Popup blocked — use Download instead");
                }
              }}
            >
              Open in new tab
            </Button>
          </div>
          {previewUrl && (
            <iframe src={previewUrl} className="flex-1 w-full rounded border" title="PDF preview" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CallIntakeForm;
